import { ApiError } from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { type FormEvent, useEffect, useState } from "react";
import { Button } from "../primitives/Button";
import { Dialog } from "../primitives/Dialog";
import { Field } from "../primitives/Field";
import { Input } from "../primitives/Input";

/**
 * "Confirm it's you": the six-digit code from the person's authenticator app
 * (ADJ-M03-10).
 *
 * The server verifies the code with the identity provider and stamps the
 * session; nothing here decides anything. What this does decide is which of
 * the refusals the person is looking at, because each has a different next
 * step: type the current code, enrol an authenticator, wait a minute, or just
 * try again.
 */

type Refusal = "wrong" | "noFactor" | "rateLimited" | "unavailable";

/** Which refusal a failed confirmation was. Exported for its test. */
export function stepUpRefusal(error: unknown): Refusal {
	if (!(error instanceof ApiError)) {
		return "unavailable";
	}
	switch (error.code) {
		case "step_up_code_invalid":
		case "invalid_argument":
			return "wrong";
		case "mfa_required":
			return "noFactor";
		case "rate_limited":
			return "rateLimited";
		default:
			return "unavailable";
	}
}

const CODE = /^[0-9]{6}$/;

export interface StepUpCodeDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Sends the code; resolves once the server has stamped the session. */
	confirm: (code: string) => Promise<unknown>;
	/** Runs once a confirmation lands, just before the dialog closes. */
	onConfirmed: () => void;
	/** Where somebody with no authenticator goes to enrol one. */
	onEnrol: () => void;
	t: Translate;
}

export function StepUpCodeDialog({
	open,
	onOpenChange,
	confirm,
	onConfirmed,
	onEnrol,
	t,
}: StepUpCodeDialogProps) {
	const [code, setCode] = useState("");
	const [busy, setBusy] = useState(false);
	const [refusal, setRefusal] = useState<Refusal | null>(null);

	useEffect(() => {
		if (open) {
			setCode("");
			setRefusal(null);
		}
	}, [open]);

	async function submit(event: FormEvent) {
		event.preventDefault();
		if (!CODE.test(code) || busy) {
			return;
		}
		setBusy(true);
		setRefusal(null);
		try {
			await confirm(code);
			// Before closing: a caller that clears its pending retry on close
			// would otherwise have nothing left to run.
			onConfirmed();
			onOpenChange(false);
		} catch (error) {
			setRefusal(stepUpRefusal(error));
			// A code is single-use in practice: the next try needs the next one.
			setCode("");
		} finally {
			setBusy(false);
		}
	}

	return (
		<Dialog
			open={open}
			onOpenChange={onOpenChange}
			title={t("patterns.stepUp.title")}
			description={t("patterns.stepUp.body")}
			size="sm"
			closeLabel={t("ui.actions.close")}
			fullScreenOnMobile
			footer={
				<>
					<Button variant="ghost" onClick={() => onOpenChange(false)}>
						{t("ui.actions.cancel")}
					</Button>
					{refusal === "noFactor" ? (
						<Button variant="primary" onClick={onEnrol}>
							{t("patterns.stepUp.enrol")}
						</Button>
					) : (
						<Button
							variant="primary"
							type="submit"
							form="step-up-code"
							loading={busy}
							disabled={!CODE.test(code)}
						>
							{t("patterns.stepUp.confirm")}
						</Button>
					)}
				</>
			}
		>
			<form id="step-up-code" onSubmit={(event) => void submit(event)}>
				<Field
					label={t("patterns.stepUp.code")}
					{...(refusal === null ? {} : { error: t(`patterns.stepUp.${refusal}`) })}
				>
					{(props) => (
						<Input
							{...props}
							value={code}
							onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
							inputMode="numeric"
							autoComplete="one-time-code"
							maxLength={6}
							dir="ltr"
							autoFocus
							className="font-mono tracking-[0.3em]"
						/>
					)}
				</Field>
			</form>
		</Dialog>
	);
}
